/**
 * Audit Record Builder
 * Creates and maintains the consultation audit trail
 */

import type {
  AuditRecord,
  PatientInput,
  Amendment,
  AnswerRecord,
  NodeType,
} from "../types";
import { generateId } from "../utils/idGenerator";
import { getPathway } from "../pathways";

/**
 * Create a new audit record for a consultation
 */
export function createAuditRecord(
  pathwayId: string,
  patient: PatientInput
): AuditRecord {
  let pathwayName: string | undefined;
  try {
    pathwayName = getPathway(pathwayId).name;
  } catch {
    // unknown pathway id — leave pathwayName undefined
  }
  return {
    consultationId: generateId("CONSULT"),
    pathwayId,
    pathwayName,
    startedAt: new Date().toISOString(),
    patient: { ...patient },
    amendments: [],
    confirmedValues: {},
    answers: [],
    flagsSet: [],
    gatewaysReached: [],
    paymentTriggered: false,
  };
}

/**
 * Record that the banner was confirmed
 */
export function recordBannerConfirmation(
  audit: AuditRecord,
  confirmedValues: Record<string, unknown>,
  amendments: Amendment[],
  extras?: { consentObtained?: boolean }
): AuditRecord {
  const now = new Date().toISOString();
  return {
    ...audit,
    bannerConfirmedAt: now,
    confirmedValues,
    amendments,
    ...(extras?.consentObtained !== undefined
      ? {
          consentObtained: extras.consentObtained,
          ...(extras.consentObtained ? { consentObtainedAt: now } : {}),
        }
      : {}),
  };
}

/**
 * Record an answer to a question
 */
export function recordAnswer(
  audit: AuditRecord,
  nodeId: string,
  nodeType: NodeType,
  answer: unknown
): AuditRecord {
  const answerRecord: AnswerRecord = {
    nodeId,
    nodeType,
    answeredAt: new Date().toISOString(),
    answer,
  };

  return {
    ...audit,
    answers: [...audit.answers, answerRecord],
  };
}

/**
 * Record that a gateway was reached
 */
export function recordGateway(
  audit: AuditRecord,
  gatewayId: string,
  gatewayType: "alarm" | "progression",
  triggersPayment: boolean
): AuditRecord {
  const record = {
    ...audit,
    gatewaysReached: [
      ...audit.gatewaysReached,
      { gatewayId, gatewayType, reachedAt: new Date().toISOString() },
    ],
  };

  // First gateway triggers payment
  if (triggersPayment && !audit.paymentTriggered) {
    record.paymentTriggered = true;
    record.paymentTriggeredAt = new Date().toISOString();
    record.paymentTriggeredBy = gatewayId;
  }

  return record;
}

/**
 * Record drug selection
 */
export function recordDrugSelection(
  audit: AuditRecord,
  pgdId: string,
  pgdVersion: string,
  ruleMatched: number,
  extra?: {
    overridden?: boolean;
    overrideReason?: string;
    recommendedPgdId?: string;
  }
): AuditRecord {
  return {
    ...audit,
    drugSelection: {
      selectedAt: new Date().toISOString(),
      selectedPgdId: pgdId,
      selectedPgdVersion: pgdVersion,
      ruleMatched,
      ...(extra || {}),
    },
  };
}

/**
 * Record PGD exclusion answer
 */
export function recordPgdExclusionAnswer(
  audit: AuditRecord,
  checkId: string,
  pgdId: string,
  category: "exclusion" | "interaction",
  questionText: string,
  answered: boolean
): AuditRecord {
  return {
    ...audit,
    pgdExclusionAnswers: [
      ...(audit.pgdExclusionAnswers || []),
      {
        checkId,
        pgdId,
        category,
        questionText,
        answered,
        answeredAt: new Date().toISOString(),
      },
    ],
  };
}

/**
 * Record the final outcome
 */
export function recordOutcome(
  audit: AuditRecord,
  outcomeId: string,
  outcomeType: string,
  reason?: string,
  title?: string,
  safetyNetting?: string[]
): AuditRecord {
  return {
    ...audit,
    completedAt: new Date().toISOString(),
    finalOutcome: {
      outcomeId,
      outcomeType: outcomeType as any,
      title,
      reason,
      safetyNetting,
      reachedAt: new Date().toISOString(),
    },
  };
}

/**
 * Record supply details
 */
export function recordSupplyDetails(
  audit: AuditRecord,
  details: {
    drugId: string;
    pgdId: string;
    pgdVersion: string;
    formulationId: string;
    doseBandId: string;
    resolvedDoseMg: number | string;
    instructionText: string;
    quantity: any;
    sugarFreeSelected: boolean;
    totalDurationDays: number;
  }
): AuditRecord {
  return {
    ...audit,
    supplyDetails: details,
  };
}
